import {
  AfterViewInit,
  Component,
  EventEmitter,
  inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { processHttpError } from "@core/services/utils/process-http-error";
import { Payment } from "@services/business/_models/interfaces/payment.interfaces";
import {
  RecordCustomActionEnum,
  RecordStatus,
} from "@services/business/_models/interfaces/record.interfaces";
import { AccessRoleEnum } from "@services/business/_models/roles";
import {
  RecordStatusData,
  RecordStatusService,
} from "@services/business/legal-records-services/record-status.service";
import { NotificationEvent } from "@ui/layout/lc-header/header-icons/notifications-panel/notification-item/types";
import { LcHeaderComponent } from "@ui/layout/lc-header/lc-header.component";
import { filter, Subject, takeUntil } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { firstValueFrom } from "rxjs";
import { FixEmailModalComponent } from "@ui/controls/modals/fix-email-modal/fix-email-modal.component";
import { RecordStatusCompactComponent } from "../legal-records/record-status-compact/record-status-compact.component";

const PAYMENT_ACTIONS = [
  RecordStatus.WAITING_FOR_PAYMENT,
  RecordStatus.WAITING_FOR_PAYMENT_FIX,
  RecordStatus.CREDIT_DOWNPAYMENT,
  RecordStatus.INFO_PAY,
];

@Component({
  selector: "app-record-header-wrapper",
  templateUrl: "./record-header-wrapper.component.html",
  imports: [LcHeaderComponent, RecordStatusCompactComponent],
})
export class RecordHeaderWrapperComponent
  implements OnInit, OnDestroy, AfterViewInit
{
  private router = inject(Router);
  private recordStatusService = inject(RecordStatusService);
  private readonly dialog = inject(MatDialog);

  @Input() public newMessages = false;
  @Input() public notifications: NotificationEvent[] = [];
  @Input() public userName = "-";
  @Input() public userSurname = "-";
  @Output() public markNotificationAsReadEvent =
    new EventEmitter<NotificationEvent>();

  protected isInRecordView = false;
  protected currentRecordId: string;
  protected recordPaymentActions: Payment[] = [];
  protected recordInfo?: RecordStatusData;
  protected isRecordStatusVisible = true;
  private intersectionObserver?: IntersectionObserver;
  private scrollListener?: () => void;
  private isIntersectionObserverSupported = false;

  private readonly destroy$ = new Subject<void>();

  ngOnInit(): void {
    this.checkIntersectionObserverSupport();
    this.checkIfInRecordView();

    this.router.events
      .pipe(takeUntil(this.destroy$))
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe(() => {
        this.checkIfInRecordView();
      });
  }

  ngAfterViewInit(): void {
    if (this.isInRecordView) {
      this.setupIntersectionObserver();
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.disconnectObserver();
    this.removeScrollListener();
  }

  private checkIfInRecordView(): void {
    const url = this.router.url;
    const recordRoutePattern =
      /\/dashboard\/operative\/records\/([A-Za-z0-9-]+)/;
    const match = RegExp(recordRoutePattern).exec(url);

    if (match) {
      this.isInRecordView = true;
      this.currentRecordId = match[1] ?? "";

      this.loadRecordData();
      setTimeout(() => this.setupIntersectionObserver(), 100);
    } else {
      this.isInRecordView = false;
      this.recordPaymentActions = [];
      this.recordInfo = undefined;
      this.disconnectObserver();
    }
  }

  private loadRecordData(): void {
    if (!this.currentRecordId) {
      return;
    }

    this.recordStatusService
      .getRecordStatusData$(this.currentRecordId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: recordInfo => {
          this.recordInfo = recordInfo;
          if (this.recordInfo) {
            this.recordPaymentActions = this.needsPaymentActions()
              ? this.recordInfo.paymentActions
              : [];
          } else {
            this.recordPaymentActions = [];
          }
        },
        error: error => {
          processHttpError(error);
          this.recordPaymentActions = [];
          this.recordInfo = undefined;
        },
      });
  }

  async onRecordStatusChange(newStatus: RecordStatus): Promise<void> {
    if (!this.currentRecordId) {
      return;
    }

    await this.recordStatusService.handleStatusChange(
      this.currentRecordId,
      newStatus
    );
    // No need to manually reload data - the observable will update automatically
  }

  onRecordPaymentAction(payment: Payment): void {
    this.recordStatusService.handlePaymentAction(payment);
  }

  async onCustomAction(action: RecordCustomActionEnum): Promise<void> {
    if (action === RecordCustomActionEnum.FIX_EMAIL) {
      const dialogRef = this.dialog.open(FixEmailModalComponent, {
        maxWidth: "100vw",
        maxHeight: "100vh",
        panelClass: "responsive-dialog",
        disableClose: true,
        data: { recordId: this.currentRecordId },
      });

      const result = await firstValueFrom(dialogRef.afterClosed());
      if (result) {
        globalThis.location.reload();
      }
    }
  }

  private needsPaymentActions(): boolean {
    if (!this.recordInfo?.availableActions) {
      return false;
    }
    return this.recordInfo.availableActions.some(
      action => PAYMENT_ACTIONS.includes(action) && !this.isReviewer
    );
  }

  private setupIntersectionObserver(): void {
    this.disconnectObserver();
    this.removeScrollListener();

    if (this.isIntersectionObserverSupported) {
      this.setupNativeIntersectionObserver();
    } else {
      this.setupScrollBasedObserver();
    }
  }

  private setupNativeIntersectionObserver(): void {
    this.intersectionObserver = new IntersectionObserver(
      entries => {
        for (const entry of entries) {
          if (entry.target.classList.contains("record-status")) {
            this.isRecordStatusVisible = entry.isIntersecting;
          }
        }
      },
      {
        threshold: 0.1,
        rootMargin: "0px",
      }
    );

    setTimeout(() => {
      const recordStatusElements = document.querySelectorAll(".record-status");
      Array.from(recordStatusElements).forEach(element => {
        this.intersectionObserver?.observe(element);
      });
    }, 100);
  }

  private setupScrollBasedObserver(): void {
    let ticking = false;

    this.scrollListener = () => {
      if (!ticking) {
        requestAnimationFrame(() => {
          this.checkElementVisibility();
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", this.scrollListener, { passive: true });
    window.addEventListener("resize", this.scrollListener, { passive: true });

    setTimeout(() => this.checkElementVisibility(), 100);
  }

  private disconnectObserver(): void {
    if (this.intersectionObserver) {
      this.intersectionObserver.disconnect();
      this.intersectionObserver = undefined;
    }
  }

  private removeScrollListener(): void {
    if (this.scrollListener) {
      window.removeEventListener("scroll", this.scrollListener);
      window.removeEventListener("resize", this.scrollListener);
      this.scrollListener = undefined;
    }
  }

  private checkIntersectionObserverSupport(): void {
    this.isIntersectionObserverSupported =
      "IntersectionObserver" in window &&
      "IntersectionObserverEntry" in window &&
      "intersectionRatio" in window.IntersectionObserverEntry.prototype;
  }

  private checkElementVisibility(): void {
    const recordStatusElements = document.querySelectorAll(".record-status");
    let anyVisible = false;

    for (const element of Array.from(recordStatusElements)) {
      const rect = element.getBoundingClientRect();
      const isVisible =
        rect.top < window.innerHeight &&
        rect.bottom > 0 &&
        rect.left < window.innerWidth &&
        rect.right > 0;

      if (isVisible) {
        anyVisible = true;
        break;
      }
    }

    this.isRecordStatusVisible = anyVisible;
  }

  protected get isReviewer(): boolean {
    return (
      this.recordInfo?.role === AccessRoleEnum.Admin ||
      this.recordInfo?.role === AccessRoleEnum.Lawyer
    );
  }

  protected get recordStatus(): RecordStatus {
    return this.recordInfo?.status ?? RecordStatus.ENGAGED;
  }

  markNotificationAsRead(notification: NotificationEvent): void {
    this.markNotificationAsReadEvent.emit(notification);
  }
}
